<template>
  <div v-loading="loading">
    <el-card shadow="never" class="info-card">
      <div v-if="current">
        <div class="section-title">当前套餐</div>
        <div class="info-grid">
          <div><span class="label">套餐</span>{{ planNameOf(current.planCode) }}</div>
          <div><span class="label">状态</span>{{ statusText(current.status) }}</div>
          <div><span class="label">开始时间</span>{{ current.startAt || '-' }}</div>
          <div><span class="label">结束时间</span>{{ formatEndAt(current) }}</div>
        </div>
      </div>
      <div v-else class="muted">暂无订阅记录</div>
    </el-card>

    <el-card shadow="never" class="pay-card">
      <div class="section-title">对公转账</div>
      <p class="tip-line">标准版 / 专业版支持微信、支付宝扫码在线支付（自动开通）。也可对公转账后点击「对公转账认款」。私有化部署请联系服务人员。</p>
      <div class="pay-account">
        <div class="pay-item">
          <span class="pay-label">收款户名</span>
          <span class="pay-value" :class="{ placeholder: !payInfo.payeeName }">{{ payInfo.payeeName || '待配置' }}</span>
          <el-button v-if="payInfo.payeeName" type="text" @click="copyText(payInfo.payeeName)">复制</el-button>
        </div>
        <div class="pay-item">
          <span class="pay-label">开户银行</span>
          <span class="pay-value" :class="{ placeholder: !payInfo.bankName }">{{ payInfo.bankName || '待配置' }}</span>
        </div>
        <div class="pay-item">
          <span class="pay-label">账号</span>
          <span class="pay-value account-no" :class="{ placeholder: !payInfo.bankAccount }">{{ payInfo.bankAccount || '待配置' }}</span>
          <el-button v-if="payInfo.bankAccount" type="text" @click="copyText(payInfo.bankAccount)">复制</el-button>
        </div>
      </div>
      <p class="muted contact">{{ payInfo.contact || '转账备注请填写企业名称；提交认款后由平台核对到账并开通。' }}</p>
      <el-alert
        v-if="!hasPayAccount"
        class="pending"
        type="info"
        :closable="false"
        title="收款账户尚未配置，请联系平台客服获取转账信息后再提交认款。"
      />
      <el-alert
        v-if="pendingNotice"
        class="pending"
        type="warning"
        :closable="false"
        :title="'已提交「' + planNameOf(pendingNotice.planCode) + '」认款，等待平台核对到账后开通'"
      />
    </el-card>

    <div class="section-title mt">套餐说明</div>
    <div class="plan-grid">
      <div
        v-for="row in displayPlans"
        :key="row.planCode"
        class="plan-card"
        :class="{
          free: row.planCode === 'FREE',
          std: row.planCode === 'STD',
          private: row.planCode === 'PRIVATE'
        }"
      >
        <div class="plan-head">
          <span class="plan-name">{{ row.planName }}</span>
          <el-tag v-if="row.planCode === 'FREE'" size="mini" type="success">注册即享</el-tag>
          <el-tag v-else-if="row.planCode === 'PRIVATE'" size="mini" type="warning">定制</el-tag>
        </div>
        <div class="plan-price">
          <template v-if="row.planCode === 'PRIVATE'">
            <span class="num contact-price">联系报价</span>
          </template>
          <template v-else-if="row.planCode === 'FREE' || !row.priceCent">
            <span class="num">0 元</span>
            <span class="unit"> / 年</span>
          </template>
          <template v-else>
            <span class="num">{{ formatPrice(row.priceCent) }}</span>
            <span class="unit"> / {{ durationText(row.durationDays) }}</span>
          </template>
        </div>
        <div class="plan-meta">{{ accountLimitText(row.maxUsers) }}</div>
        <ul class="features">
          <li v-for="f in (row.features || [])" :key="f">{{ f }}</li>
        </ul>
        <div v-if="row.planCode === 'STD' || row.planCode === 'PRO'" class="plan-actions">
          <el-button
            type="primary"
            size="small"
            :loading="payLoading && payTargetCode === row.planCode"
            @click="openOnlinePay(row)"
          >在线支付</el-button>
          <el-button
            size="small"
            :disabled="!!pendingNotice"
            @click="openNotice(row)"
          >对公转账认款</el-button>
        </div>
        <el-button
          v-else-if="row.planCode === 'PRIVATE'"
          type="primary"
          plain
          size="small"
          @click="contactPrivate"
        >联系服务人员</el-button>
        <span v-else-if="row.planCode === 'FREE'" class="trial-action">新注册企业自动开通</span>
        <span v-else class="trial-action">-</span>
      </div>
    </div>

    <div class="section-title mt">订阅历史</div>
    <el-table :data="history" border>
      <el-table-column label="套餐" width="120">
        <template slot-scope="{ row }">{{ row.planName || planNameOf(row.planCode) }}</template>
      </el-table-column>
      <el-table-column label="开通时价格" width="120">
        <template slot-scope="{ row }">
          <span v-if="row.planCode === 'TRIAL' || row.planCode === 'PRIVATE' || row.priceCent === 0">
            {{ row.planCode === 'PRIVATE' ? '联系报价' : '免费' }}
          </span>
          <span v-else>{{ formatPrice(row.priceCent) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="时长" width="90">
        <template slot-scope="{ row }">{{ durationText(row.durationDays) }}</template>
      </el-table-column>
      <el-table-column label="账号数" width="90">
        <template slot-scope="{ row }">{{ accountLimitText(row.maxUsers, true) }}</template>
      </el-table-column>
      <el-table-column label="开通时权益" min-width="260">
        <template slot-scope="{ row }">
          <ul v-if="row.features && row.features.length" class="hist-features">
            <li v-for="(f, i) in row.features" :key="i">{{ f }}</li>
          </ul>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="{ row }">{{ statusText(row.status) }}</template>
      </el-table-column>
      <el-table-column prop="startAt" label="开始时间" min-width="160" />
      <el-table-column label="结束时间" min-width="160">
        <template slot-scope="{ row }">{{ formatEndAt(row) }}</template>
      </el-table-column>
      <el-table-column label="开票" width="140" fixed="right">
        <template slot-scope="{ row }">
          <el-button
            v-if="row.canApplyInvoice"
            type="text"
            @click="openInvoice(row)"
          >申请开票</el-button>
          <template v-else-if="row.invoiceApply">
            <span v-if="row.invoiceApply.status === 'PENDING'" class="muted">开票中</span>
            <el-button
              v-else-if="row.invoiceApply.status === 'ISSUED' && row.invoiceApply.invoiceFileUrl"
              type="text"
              @click="downloadInvoice(row.invoiceApply)"
            >下载发票</el-button>
            <span v-else class="muted">-</span>
          </template>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="提交对公转账认款" :visible.sync="noticeVisible" width="480px" append-to-body>
      <p class="dialog-tip">提交后不会立即开通，需平台核对银行到账后再开通套餐。</p>
      <el-form :model="noticeForm" label-width="100px">
        <el-form-item label="套餐">{{ noticeForm.planName }} · {{ formatPrice(noticeForm.priceCent) }} / 年</el-form-item>
        <el-form-item label="汇款户名">
          <el-input v-model="noticeForm.payerName" placeholder="对公账户名称" />
        </el-form-item>
        <el-form-item label="流水号">
          <el-input v-model="noticeForm.transferNo" placeholder="银行回单号，选填" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="noticeForm.remark" type="textarea" :rows="2" placeholder="转账时间、联系人等" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="noticeVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitNotice">提交认款</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="payDialogTitle"
      :visible.sync="payVisible"
      width="440px"
      append-to-body
      @close="stopPayPoll"
    >
      <div v-if="payStep === 'quote'" class="pay-quote">
        <p class="dialog-tip">{{ payQuote.summary || '确认应付金额后选择支付方式' }}</p>
        <el-form label-width="100px" size="small">
          <el-form-item label="类型">{{ payQuote.quoteTypeLabel || '-' }}</el-form-item>
          <el-form-item label="目标套餐">{{ payQuote.targetPlanName || '-' }}</el-form-item>
          <el-form-item v-if="payQuote.fromPlanName" label="当前套餐">{{ payQuote.fromPlanName }}</el-form-item>
          <el-form-item label="套餐原价">{{ formatPriceCent(payQuote.listPriceCent) }}</el-form-item>
          <el-form-item v-if="Number(payQuote.creditCent) > 0" label="剩余折抵">
            − {{ formatPriceCent(payQuote.creditCent) }}
            <span v-if="payQuote.remainingDays" class="muted">（剩 {{ payQuote.remainingDays }} 天）</span>
          </el-form-item>
          <el-form-item label="应付金额">
            <span class="pay-amount">{{ formatPriceCent(payQuote.payCent) }}</span>
          </el-form-item>
          <el-form-item v-if="payQuote.newEndAt" label="开通后到期">{{ payQuote.newEndAt }}</el-form-item>
          <el-form-item label="支付方式">
            <el-radio-group v-model="payChannel">
              <el-radio label="WECHAT">微信支付</el-radio>
              <el-radio label="ALIPAY">支付宝</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </div>
      <div v-else-if="payStep === 'qr'" class="pay-qr-box">
        <p class="dialog-tip">{{ payChannelTip }}</p>
        <div class="pay-amount-line">应付 <strong>{{ formatPriceCent(payOrder.payCent) }}</strong></div>
        <img v-if="payQrDataUrl" :src="payQrDataUrl" class="pay-qr-img" :alt="payChannelLabel + '支付二维码'" />
        <p class="muted pay-order-no">订单号 {{ payOrder.orderNo }}</p>
        <p v-if="payStatusText" class="pay-status">{{ payStatusText }}</p>
      </div>
      <div v-else-if="payStep === 'alipay'" class="pay-qr-box">
        <p class="dialog-tip">已打开支付宝收银台，请在新窗口完成支付；支付成功后本页将自动开通</p>
        <div class="pay-amount-line">应付 <strong>{{ formatPriceCent(payOrder.payCent) }}</strong></div>
        <p class="muted pay-order-no">订单号 {{ payOrder.orderNo }}</p>
        <p v-if="payStatusText" class="pay-status">{{ payStatusText }}</p>
        <el-button type="text" @click="reopenAlipayCashier">若未弹出窗口，点击重新打开收银台</el-button>
      </div>
      <span slot="footer">
        <el-button @click="payVisible = false">{{ payStep === 'quote' ? '取消' : '关闭' }}</el-button>
        <el-button
          v-if="payStep === 'quote'"
          type="primary"
          :loading="payCreating"
          @click="createPayOrder"
        >{{ payChannel === 'ALIPAY' ? '前往支付宝支付' : '生成支付二维码' }}</el-button>
      </span>
    </el-dialog>

    <el-dialog title="申请开票" :visible.sync="invoiceVisible" width="560px" append-to-body>
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="只开 1% 的增值税普通发票"
        style="margin-bottom:14px"
      />
      <el-form :model="invoiceForm" label-width="110px">
        <el-form-item label="套餐">{{ invoiceForm.planName }} · {{ formatPrice(invoiceForm.amountCent) }}</el-form-item>
        <el-form-item label="发票抬头" required>
          <el-input v-model="invoiceForm.invoiceTitle" placeholder="与营业执照名称一致" />
        </el-form-item>
        <el-form-item label="纳税人识别号" required>
          <el-input v-model="invoiceForm.invoiceTaxNo" placeholder="统一社会信用代码" />
        </el-form-item>
        <el-form-item label="开票地址">
          <el-input v-model="invoiceForm.invoiceAddress" placeholder="选填" />
        </el-form-item>
        <el-form-item label="开票电话">
          <el-input v-model="invoiceForm.invoicePhone" placeholder="选填" />
        </el-form-item>
        <el-form-item label="开户银行">
          <el-input v-model="invoiceForm.invoiceBankName" placeholder="选填" />
        </el-form-item>
        <el-form-item label="银行账号">
          <el-input v-model="invoiceForm.invoiceBankAccount" placeholder="选填" />
        </el-form-item>
        <el-form-item label="收票邮箱">
          <el-input v-model="invoiceForm.invoiceEmail" placeholder="选填" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="invoiceForm.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="invoiceVisible = false">取消</el-button>
        <el-button type="primary" :loading="invoiceSubmitting" @click="submitInvoice">提交申请</el-button>
      </span>
    </el-dialog>

    <el-dialog title="联系服务人员" :visible.sync="wechatQrVisible" width="360px" center append-to-body>
      <div class="wechat-qr-box">
        <img v-if="wechatQrUrl" :src="wechatQrUrl" class="wechat-qr-img" alt="客服微信二维码" />
        <el-alert
          v-else
          type="info"
          :closable="false"
          show-icon
          title="暂未配置客服微信二维码，请联系平台获取咨询方式。"
        />
        <p class="wechat-qr-tip">请使用微信扫一扫，添加客服咨询私有化部署方案与报价</p>
      </div>
      <span slot="footer">
        <el-button type="primary" @click="wechatQrVisible = false">知道了</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getSubscription, submitPayNotice, getCompany, submitInvoiceApply, getSubscriptionQuote, createSubscriptionOrder, getSubscriptionOrder } from '../../api/sys'
import QRCode from 'qrcode'

export default {
  name: 'SysSubscription',
  data() {
    return {
      loading: false,
      submitting: false,
      invoiceSubmitting: false,
      noticeVisible: false,
      invoiceVisible: false,
      wechatQrVisible: false,
      payVisible: false,
      payLoading: false,
      payCreating: false,
      payTargetCode: '',
      payStep: 'quote',
      payQuote: {},
      payOrder: {},
      payQrDataUrl: '',
      payFormHtml: '',
      payStatusText: '',
      payPollTimer: null,
      payChannel: 'WECHAT',
      current: null,
      plans: [],
      history: [],
      pendingNotice: null,
      payInfo: {},
      noticeForm: {
        planCode: '',
        planName: '',
        priceCent: 0,
        payerName: '',
        transferNo: '',
        remark: ''
      },
      invoiceForm: {
        subscriptionId: null,
        planName: '',
        amountCent: 0,
        invoiceTitle: '',
        invoiceTaxNo: '',
        invoiceAddress: '',
        invoicePhone: '',
        invoiceBankName: '',
        invoiceBankAccount: '',
        invoiceEmail: '',
        remark: ''
      }
    }
  },
  computed: {
    displayPlans() {
      const order = { FREE: 1, STD: 2, PRO: 3, PRIVATE: 4 }
      return (this.plans || [])
        .slice()
        .filter(p => ['FREE', 'STD', 'PRO', 'PRIVATE'].includes(p.planCode))
        .sort((a, b) => (order[a.planCode] || 99) - (order[b.planCode] || 99))
    },
    hasPayAccount() {
      const p = this.payInfo || {}
      return !!(p.payeeName || p.bankAccount)
    },
    wechatQrUrl() {
      // 私有化部署「联系服务人员」统一使用站点静态二维码
      return '/site/service-wechat-qr.png'
    },
    payDialogTitle() {
      if (this.payStep === 'qr') return this.payChannelLabel + '扫码支付'
      if (this.payStep === 'alipay') return '支付宝收银台支付'
      return '确认支付金额'
    },
    payChannelLabel() {
      return this.payChannel === 'ALIPAY' ? '支付宝' : '微信'
    },
    payChannelTip() {
      if (this.payChannel === 'ALIPAY') {
        return '将跳转支付宝收银台完成支付，支付成功后将自动开通'
      }
      return '请使用微信扫一扫完成支付，支付成功后将自动开通'
    }
  },
  beforeDestroy() {
    this.stopPayPoll()
  },
  created() {
    this.load()
  },
  methods: {
    statusText(status) {
      const map = { TRIAL: '试用中', ACTIVE: '已开通', EXPIRED: '已到期', DISABLED: '已禁用', PENDING: '待认款' }
      return map[status] || status || '-'
    },
    formatPrice(cent) {
      if (cent == null) return '-'
      const n = Number(cent) / 100
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' 元'
    },
    formatPriceCent(cent) {
      if (cent == null) return '-'
      const n = Number(cent) / 100
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' 元'
    },
    durationText(days) {
      if (days == null || days === '') return '-'
      if (Number(days) === 0) return '长期'
      if (days >= 360) return '年'
      return days + ' 天'
    },
    formatEndAt(row) {
      if (!row) return '-'
      if (row.planCode === 'PRIVATE' || row.planCode === 'FREE' || Number(row.durationDays) === 0) return '长期'
      return row.endAt || '-'
    },
    accountLimitText(maxUsers, compact) {
      if (maxUsers == null) return '-'
      if (Number(maxUsers) <= 0) return compact ? '不限' : '账号不限'
      return compact ? (maxUsers + ' 个') : ('含 ' + maxUsers + ' 个账号')
    },
    planNameOf(code) {
      const hit = (this.plans || []).find(p => p.planCode === code)
      return (hit && hit.planName) || code || '-'
    },
    contactPrivate() {
      this.wechatQrVisible = true
    },
    async load() {
      this.loading = true
      try {
        const res = await getSubscription()
        const data = res.data || {}
        this.current = data.current || null
        this.plans = data.plans || []
        this.history = data.history || []
        this.pendingNotice = data.pendingNotice || null
        this.payInfo = data.payInfo || {}
      } finally {
        this.loading = false
      }
    },
    async openOnlinePay(row) {
      if (!row || !row.planCode) return
      this.payTargetCode = row.planCode
      this.payLoading = true
      this.payStep = 'quote'
      this.payQuote = {}
      this.payOrder = {}
      this.payQrDataUrl = ''
      this.payFormHtml = ''
      this.payStatusText = ''
      this.payChannel = 'WECHAT'
      this.stopPayPoll()
      try {
        const res = await getSubscriptionQuote(row.planCode)
        this.payQuote = res.data || {}
        this.payVisible = true
      } finally {
        this.payLoading = false
        this.payTargetCode = ''
      }
    },
    async createPayOrder() {
      if (!this.payQuote.targetPlanCode) return
      this.payCreating = true
      try {
        const res = await createSubscriptionOrder({
          planCode: this.payQuote.targetPlanCode,
          channel: this.payChannel
        })
        const order = res.data || {}
        this.payOrder = order
        if (order.channel) this.payChannel = order.channel
        if (order.status === 'PAID') {
          this.payVisible = false
          this.$message.success('已开通套餐')
          this.load()
          return
        }
        if (order.payMode === 'PAGE' || order.payFormHtml) {
          this.payFormHtml = order.payFormHtml || ''
          if (!this.payFormHtml) {
            this.$message.error('未获取到支付宝支付表单')
            return
          }
          this.payStep = 'alipay'
          this.payStatusText = '等待支付…'
          this.openAlipayCashier(this.payFormHtml)
          this.startPayPoll(order.orderNo)
          return
        }
        if (!order.codeUrl) {
          this.$message.error('未获取到支付二维码')
          return
        }
        this.payQrDataUrl = await QRCode.toDataURL(order.codeUrl, {
          width: 220,
          margin: 1,
          errorCorrectionLevel: 'M'
        })
        this.payStep = 'qr'
        this.payStatusText = '等待支付…'
        this.startPayPoll(order.orderNo)
      } finally {
        this.payCreating = false
      }
    },
    openAlipayCashier(formHtml) {
      if (!formHtml) return
      const win = window.open('', '_blank')
      if (!win) {
        this.$message.warning('浏览器拦截了弹窗，请允许后点击「重新打开收银台」')
        return
      }
      win.document.open()
      win.document.write(formHtml)
      win.document.close()
    },
    reopenAlipayCashier() {
      this.openAlipayCashier(this.payFormHtml)
    },
    startPayPoll(orderNo) {
      this.stopPayPoll()
      this.payPollTimer = setInterval(async () => {
        try {
          const res = await getSubscriptionOrder(orderNo)
          const order = res.data || {}
          if (order.status === 'PAID') {
            this.stopPayPoll()
            this.payStatusText = '支付成功，套餐已开通'
            this.$message.success('支付成功，套餐已开通')
            this.payVisible = false
            this.load()
          } else if (order.status === 'CLOSED' || order.status === 'FAILED') {
            this.stopPayPoll()
            this.payStatusText = '订单已关闭，请重新发起支付'
          }
        } catch (e) { /* ignore poll errors */ }
      }, 2000)
    },
    stopPayPoll() {
      if (this.payPollTimer) {
        clearInterval(this.payPollTimer)
        this.payPollTimer = null
      }
    },
    openNotice(row) {
      this.noticeForm = {
        planCode: row.planCode,
        planName: row.planName,
        priceCent: row.priceCent,
        payerName: '',
        transferNo: '',
        remark: ''
      }
      this.noticeVisible = true
    },
    async submitNotice() {
      if (!this.noticeForm.planCode) return
      this.submitting = true
      try {
        await submitPayNotice({
          planCode: this.noticeForm.planCode,
          payerName: this.noticeForm.payerName,
          transferNo: this.noticeForm.transferNo,
          remark: this.noticeForm.remark
        })
        this.noticeVisible = false
        this.$message.success('已提交认款，到账后将为您开通')
        this.load()
      } finally {
        this.submitting = false
      }
    },
    async openInvoice(row) {
      let company = {}
      try {
        const res = await getCompany()
        company = res.data || {}
      } catch (e) { /* ignore */ }
      this.invoiceForm = {
        subscriptionId: row.id,
        planName: row.planName || this.planNameOf(row.planCode),
        amountCent: row.priceCent || 0,
        invoiceTitle: company.invoiceTitle || company.companyName || '',
        invoiceTaxNo: company.invoiceTaxNo || '',
        invoiceAddress: company.invoiceAddress || company.address || '',
        invoicePhone: company.invoicePhone || company.contactPhone || '',
        invoiceBankName: company.invoiceBankName || '',
        invoiceBankAccount: company.invoiceBankAccount || '',
        invoiceEmail: company.invoiceEmail || '',
        remark: ''
      }
      this.invoiceVisible = true
    },
    async submitInvoice() {
      if (!this.invoiceForm.subscriptionId) return
      if (!this.invoiceForm.invoiceTitle) return this.$message.warning('请填写发票抬头')
      if (!this.invoiceForm.invoiceTaxNo) return this.$message.warning('请填写纳税人识别号')
      this.invoiceSubmitting = true
      try {
        await submitInvoiceApply({
          subscriptionId: this.invoiceForm.subscriptionId,
          invoiceTitle: this.invoiceForm.invoiceTitle,
          invoiceTaxNo: this.invoiceForm.invoiceTaxNo,
          invoiceAddress: this.invoiceForm.invoiceAddress,
          invoicePhone: this.invoiceForm.invoicePhone,
          invoiceBankName: this.invoiceForm.invoiceBankName,
          invoiceBankAccount: this.invoiceForm.invoiceBankAccount,
          invoiceEmail: this.invoiceForm.invoiceEmail,
          remark: this.invoiceForm.remark
        })
        this.invoiceVisible = false
        this.$message.success('已提交开票申请')
        this.load()
      } finally {
        this.invoiceSubmitting = false
      }
    },
    downloadInvoice(apply) {
      const url = apply && apply.invoiceFileUrl
      if (!url) return this.$message.warning('暂无发票文件')
      window.open(url, '_blank')
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('已复制'))
        return
      }
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      this.$message.success('已复制')
    }
  }
}
</script>

<style scoped>
.info-card, .pay-card { border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 16px; }
.info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 10px 16px; }
.info-grid .label { display: inline-block; min-width: 72px; color: #94a3b8; margin-right: 8px; }
.section-title { font-weight: 600; color: #0f172a; margin-bottom: 10px; }
.section-title.mt { margin-top: 20px; }
.muted { color: #94a3b8; font-size: 13px; }
.tip-line { margin: 0 0 12px; color: #64748b; font-size: 13px; line-height: 1.6; }
.contact { margin: 10px 0 0; }
.pending { margin-top: 12px; }
.pay-account {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 40px;
}
.pay-item {
  display: flex;
  align-items: center;
  min-width: 0;
}
.pay-label {
  color: #94a3b8;
  margin-right: 10px;
  white-space: nowrap;
}
.pay-value {
  color: #0f172a;
  white-space: nowrap;
}
.pay-value.account-no {
  font-family: Consolas, Menlo, monospace;
  letter-spacing: 0.04em;
}
.pay-account .placeholder { color: #cbd5e1; }
.pay-item .el-button { padding: 0; margin-left: 8px; }
.plan-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; margin-bottom: 8px; }
.plan-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.plan-card.free { background: #f8fafc; }
.plan-card.std { background: #fff; }
.plan-card.private { background: #fffbeb; border-color: #fde68a; }
.plan-price .contact-price { font-size: 20px; font-weight: 800; color: #b45309; }
.plan-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.plan-name { font-size: 16px; font-weight: 700; color: #0f172a; }
.plan-price { color: #0f172a; margin-bottom: 4px; }
.plan-price .num { font-size: 22px; font-weight: 800; }
.plan-price .unit { color: #64748b; font-size: 13px; }
.plan-meta { color: #64748b; font-size: 12px; margin-bottom: 10px; }
.features { margin: 0 0 14px; padding-left: 18px; color: #475569; font-size: 13px; line-height: 1.7; flex: 1; }
.trial-action { display: inline-block; margin-top: auto; color: #94a3b8; font-size: 13px; }
.plan-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
}
.pay-amount { font-size: 20px; font-weight: 800; color: #0f172a; }
.pay-quote .el-form-item { margin-bottom: 8px; }
.pay-qr-box { text-align: center; }
.pay-amount-line { margin-bottom: 12px; color: #334155; }
.pay-qr-img {
  width: 220px;
  height: 220px;
  object-fit: contain;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}
.pay-order-no { margin: 10px 0 0; font-size: 12px; }
.pay-status { margin: 8px 0 0; color: #2563eb; font-size: 13px; }
.dialog-tip { margin: 0 0 12px; color: #64748b; font-size: 13px; }
.hist-features {
  margin: 0;
  padding-left: 16px;
  color: #475569;
  font-size: 12px;
  line-height: 1.55;
}
.wechat-qr-box { text-align: center; padding: 4px 0 0; }
.wechat-qr-img {
  width: 220px;
  height: 220px;
  object-fit: contain;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}
.wechat-qr-tip {
  margin: 14px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}
</style>
